import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useActiveRequestCount } from '../features/request-tracking/hooks/useActiveRequestCount';
import { usePortalProjectOptional } from './PortalProjectContext';
import { getCustomerId, getProjectId } from '../utils/cookies';

export interface OpenRequestTrackerOptions {
  /** Bumps panel refresh (e.g. after starting an import). */
  refreshSignal?: number | string;
}

export interface RequestTrackingModalContextValue {
  opened: boolean;
  refreshSignal?: number | string;
  /** Queued + running requests for the current project scope. */
  activeRequestCount: number;
  setActiveRequestCount: (count: number) => void;
  /** Fetch latest queued + running count (e.g. hover on header icon). */
  refreshActiveRequestCount: () => void;
  openRequestTracker: (options?: OpenRequestTrackerOptions) => void;
  closeRequestTracker: () => void;
}

const RequestTrackingModalContext = createContext<RequestTrackingModalContextValue | null>(
  null,
);

export function RequestTrackingModalProvider({ children }: { children: ReactNode }) {
  const [opened, setOpened] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState<number | string | undefined>();
  const [activeRequestCount, setActiveRequestCount] = useState(0);
  const refreshBadgeRef = useRef<() => Promise<void>>(async () => {});
  const portalProject = usePortalProjectOptional();
  const projectScopeKey = `${getCustomerId()}|${portalProject?.projectId ?? getProjectId()}`;

  const registerBadgeRefresh = useCallback((refresh: () => Promise<void>) => {
    refreshBadgeRef.current = refresh;
  }, []);

  useActiveRequestCount({
    enabled: !opened,
    refreshSignal,
    projectScopeKey,
    refreshOnWindowFocus: false,
    onChange: setActiveRequestCount,
    onRegisterRefresh: registerBadgeRefresh,
  });

  const refreshActiveRequestCount = useCallback(() => {
    void refreshBadgeRef.current();
  }, []);

  const openRequestTracker = useCallback((options?: OpenRequestTrackerOptions) => {
    if (options?.refreshSignal != null) {
      setRefreshSignal(options.refreshSignal);
    }
    setOpened(true);
  }, []);

  const closeRequestTracker = useCallback(() => {
    setOpened(false);
    void refreshBadgeRef.current();
  }, []);

  const value = useMemo<RequestTrackingModalContextValue>(
    () => ({
      opened,
      refreshSignal,
      activeRequestCount,
      setActiveRequestCount,
      refreshActiveRequestCount,
      openRequestTracker,
      closeRequestTracker,
    }),
    [
      opened,
      refreshSignal,
      activeRequestCount,
      refreshActiveRequestCount,
      openRequestTracker,
      closeRequestTracker,
    ],
  );

  return (
    <RequestTrackingModalContext.Provider value={value}>
      {children}
    </RequestTrackingModalContext.Provider>
  );
}

export function useRequestTrackingModal(): RequestTrackingModalContextValue {
  const ctx = useContext(RequestTrackingModalContext);
  if (!ctx) {
    throw new Error('useRequestTrackingModal must be used within RequestTrackingModalProvider');
  }
  return ctx;
}

/** Safe when provider is absent (e.g. tests). */
export function useRequestTrackingModalOptional(): RequestTrackingModalContextValue | null {
  return useContext(RequestTrackingModalContext);
}
